import { useEffect, useRef, useState } from 'react'
import { AlertCircle, ExternalLink, X } from 'lucide-react'
import {
  pdfBase64ToObjectUrl,
  timetableDataQualityApi,
  type TeacherContextPdf,
} from '@/services/api/timetableDataQuality'
import { ApiErrorResponse } from '@/services/api/client'
import { extractErrorMessage } from '@/utils/errorHandler'
import { ProgressDialog } from '@/components/common/ProgressDialog'

const KEEP_ALIVE_AFTER_OPEN_MS = 5 * 60 * 1000

interface PdfTimetableModalProps {
  teacherId: string
  batchId?: string | null
  existingUrl?: string | null
  pdfMeta?: TeacherContextPdf | null
  onClose: () => void
}

export function PdfTimetableModal({ teacherId, batchId, existingUrl, pdfMeta, onClose }: PdfTimetableModalProps) {
  const [pdf, setPdf] = useState<TeacherContextPdf | null>(null)
  const [noPdf, setNoPdf] = useState(false)
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const keepAliveUntilRef = useRef<number>(0)
  const objectUrlRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (existingUrl) {
      setObjectUrl(existingUrl)
      setPdf(pdfMeta ?? null)
      setNoPdf(false)
      setError(null)
      setLoading(false)
      return () => {
        cancelled = true
      }
    }
    setLoading(true)
    setObjectUrl(null)
    setPdf(null)
    setNoPdf(false)
    setError(null)
    void (async () => {
      try {
        const context = await timetableDataQualityApi.getTeacherContext(teacherId, batchId ?? undefined)
        if (cancelled) return
        if (!context.pdf) {
          setNoPdf(true)
          setLoading(false)
          return
        }
        setPdf(context.pdf)
        if (!context.pdf.page) {
          setLoading(false)
          return
        }
        const page = await timetableDataQualityApi.getPdfPage(
          context.pdf.file_id,
          context.pdf.page,
        )
        if (page.page_number !== context.pdf.page) {
          if (!cancelled) setLoading(false)
          return
        }
        const url = pdfBase64ToObjectUrl(page.pdf_base64)
        if (cancelled) {
          URL.revokeObjectURL(url)
          return
        }
        objectUrlRef.current = url
        setObjectUrl(url)
        setLoading(false)
      } catch (err) {
        if (!cancelled) {
          if (err instanceof ApiErrorResponse && err.statusCode === 422) {
            setLoading(false)
            return
          }
          setError(extractErrorMessage(err, { loadError: 'PDF konnte nicht geladen werden' }))
          setLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
      const url = objectUrlRef.current
      if (!url) return
      objectUrlRef.current = null
      if (Date.now() < keepAliveUntilRef.current) {
        setTimeout(() => URL.revokeObjectURL(url), KEEP_ALIVE_AFTER_OPEN_MS)
      } else {
        URL.revokeObjectURL(url)
      }
    }
  }, [teacherId, batchId, existingUrl, pdfMeta])

  const handleOpenInTab = () => {
    if (!objectUrl) return
    keepAliveUntilRef.current = Date.now() + KEEP_ALIVE_AFTER_OPEN_MS
    window.open(objectUrl, '_blank')
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg w-full max-w-[96vw] h-[95vh] max-h-[95vh] flex flex-col">
        <div className="p-5 pb-3 flex items-center justify-between shrink-0 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-semibold">
              PDF-Stundenplan{pdf ? `: ${pdf.original_filename ?? 'PDF'}` : ''}
            </h3>
            {pdf && (
              <p className="text-sm text-gray-500 mt-0.5">
                {pdf.source === 'bulk_mapping'
                  ? pdf.page
                    ? `Seite ${pdf.page} (Bulk-Import-Mapping)`
                    : 'Import-Mapping vorhanden, aber Seite unbekannt'
                  : 'Kein Import-Mapping — Seite unbekannt'}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {objectUrl && (
              <button
                onClick={handleOpenInTab}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <ExternalLink className="w-4 h-4" />
                In neuem Tab öffnen
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
        <div className="p-4 flex-1 min-h-0 flex items-stretch justify-center">
          {loading ? (
            <div className="flex items-center justify-center text-gray-500 py-16">PDF wird geladen…</div>
          ) : error ? (
            <div className="flex items-center justify-center text-red-600 text-sm py-16">
              <AlertCircle className="w-5 h-5 mr-2" />
              {error}
            </div>
          ) : noPdf ? (
            <div className="flex items-center justify-center text-gray-500 text-sm py-16">
              Kein PDF-Stundenplan vorhanden (weder Import-Mapping noch Upload der Kategorie „Stundenplan“).
            </div>
          ) : !objectUrl ? (
            <div className="flex items-center justify-center text-gray-500 text-sm py-16">
              Seite unbekannt — das Sammel-PDF wird nicht geöffnet.
            </div>
          ) : (
            <iframe src={`${objectUrl}#zoom=page-width`} title="PDF-Stundenplan" className="w-full h-full min-h-0 flex-1 border border-gray-200 rounded-lg" />
          )}
        </div>
      </div>
      {loading && <ProgressDialog title="PDF wird geladen…" current={0} total={0} />}
    </div>
  )
}
